import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
const NavLink = ({
  to,
  children,
  className,
  onClick
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => <Link to={to} onClick={onClick} className={cn("relative group inline-block text-sm uppercase tracking-widest text-foreground font-sans font-medium py-1 transition-colors hover:text-foreground/70", className)}>
    {children}
    <span className="absolute bottom-0 left-1/2 w-0 h-[1px] bg-secondary transition-all duration-300 ease-out -translate-x-1/2 group-hover:w-full" />
  </Link>;
function HeaderContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const midPoint = Math.ceil((menuItems?.length || 0) / 2);
  const leftItems = menuItems?.slice(0, midPoint) || [];
  const rightItems = menuItems?.slice(midPoint) || [];
  return <>
      <header data-section-id="39533" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-md shadow-sm border-border/20" : "bg-background")}>
        <div className="container mx-auto px-6 h-20 flex items-center justify-center relative">
          {}
          <nav className="hidden md:flex items-center gap-8 absolute left-6 lg:left-12">
            {!loading && leftItems.map((item, i) => <div key={item.id} data-index={i}>
                  <NavLink to={item.href || "#"}>{item.label}</NavLink>
                </div>)}
          </nav>

          {}
          <div className="z-10 flex-shrink-0">
            <Link to="/HomePage" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/echelon_soiree_logo.png" alt="Echelon Soiree" className="h-7 w-auto object-contain" />
            </Link>
          </div>

          {}
          <nav className="hidden md:flex items-center gap-8 absolute right-6 lg:right-12">
            {!loading && rightItems.map((item, i) => <div key={item.id} data-index={i + midPoint}>
                  <NavLink to={item.href || "#"}>{item.label}</NavLink>
                </div>)}
          </nav>

          {}
          <div className="absolute right-6 md:hidden">
            <Button variant="ghost" onClick={() => setIsMobileMenuOpen(true)} className="text-sm uppercase tracking-widest font-sans font-medium text-foreground hover:text-foreground/70 hover:bg-transparent p-0 h-auto">
              Menu
            </Button>
          </div>
        </div>
      </header>

      {}
      <div className={cn("fixed inset-0 z-[60] bg-background flex flex-col items-center justify-center transition-all duration-500 ease-in-out", isMobileMenuOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4")}>
        {}
        <div className="absolute top-6 right-6">
          <Button variant="ghost" onClick={() => setIsMobileMenuOpen(false)} className="text-sm uppercase tracking-widest font-sans font-medium text-foreground hover:text-foreground/70 hover:bg-transparent flex items-center gap-2 p-0 h-auto">
            Close <X className="h-4 w-4" />
          </Button>
        </div>

        {}
        <div className="absolute top-6 left-6">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582385/img/echelon_soiree_logo.png" alt="Echelon Soiree" className="h-6 w-auto opacity-80" />
        </div>

        {}
        <nav className="flex flex-col items-center gap-8 p-8 text-center">
          {!loading && menuItems.map((item, i) => <div key={item.id} data-index={i} className="overflow-hidden">
                <Link to={item.href || "#"} onClick={() => setIsMobileMenuOpen(false)} className="block text-3xl font-serif text-foreground hover:text-secondary transition-colors duration-300">
                  {item.label}
                </Link>
              </div>)}
          {loading && <div className="flex flex-col gap-4 animate-pulse">
              <div className="h-8 w-32 bg-muted rounded" />
              <div className="h-8 w-24 bg-muted rounded" />
              <div className="h-8 w-28 bg-muted rounded" />
            </div>}
        </nav>
      </div>
    </>;
}
export default function Header() {
  return <MenuProvider menu_id="45" className="w-full">
      <HeaderContent />
    </MenuProvider>;
}